import { useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import clsx from "clsx";
import { ArrowLeft, ArrowRight } from "@gravity-ui/icons";
import { CaseCard } from "./CaseChapter";
import SlideMount from "./SlideMount";
import TouchLoupe from "./TouchLoupe";
import type { CaseView } from "./caseView";
import type { StoryMode } from "./useStoryMode";
import styles from "./CaseSpread.module.scss";

/* UI-DESIGN: LB-59 (Avi) the three cases arrive together and the next
   scroll is Host. One chapter replaces the three case stops: in the live
   scene the three case files stand up side by side on the light
   (StoryScene's "cases" places) above their three cards; Look closer, or
   clicking a file, brings that one forward and centred while the others
   dim, and the loupe still reads each file. The static stack shows the
   three mounts in the same row; phones stack each case's pre-rendered
   frame over its card. LB-77 (Taco): no magnifier button on these cards;
   clicking a file still brings it forward. Try this case and Preview a
   role on every card. */
type CaseSpreadProps = {
  cases: CaseView[];
  mode: StoryMode;
  frames: (view: CaseView) => string | null;
  selectedId: string | null;
  active: boolean;
  onPreview: (view: CaseView) => void;
  onHost: (view: CaseView) => void;
  canHost: boolean;
};

type StackProps = Omit<CaseSpreadProps, "mode" | "selectedId" | "active">;

const SWIPE = 60; // horizontal travel that deals the next file, px
const DECIDE = 10; // travel before a press counts as a swipe, px
const HOLD = 280; // TouchLoupe's press-and-hold; a press still that long is the lens, not a swipe

/* UI-DESIGN: LB-74 (Taco's B pick, 2026-10-18) phones browse the cases as
   one stack of files: the top file is the case, the next two show under
   it, a sideways swipe or the arrows deal the next one, and its card
   follows straight under the stack. Press and hold still lifts the
   magnifier (LB-71): a press that stays put that long is the lens, so the
   stack only takes presses that move sideways first. */
const PhoneStack = ({ cases, frames, onPreview, onHost, canHost }: StackProps) => {
  const [at, setAt] = useState(0);
  const [dx, setDx] = useState(0);
  const [leavingFrame, setLeavingFrame] = useState<string | null>(null);
  const press = useRef<{ x: number; y: number; t: number; swiping: boolean } | null>(null);
  /* UI-DESIGN: keep the outgoing artwork visible during the dissolve; no pop/scale. */
  const go = (step: number) => {
    setLeavingFrame(frames(cases[at % cases.length]));
    setAt((n) => (n + step + cases.length) % cases.length);
    setDx(0);
  };
  const view = cases[at % cases.length];
  const behind = [2, 1]
    .filter((k) => k < cases.length)
    .map((k) => cases[(at + k) % cases.length]);

  const onDown = (e: ReactPointerEvent) => {
    press.current = { x: e.clientX, y: e.clientY, t: e.timeStamp, swiping: false };
  };
  const onMove = (e: ReactPointerEvent) => {
    const p = press.current;
    if (!p) return;
    const x = e.clientX - p.x;
    if (!p.swiping) {
      const y = e.clientY - p.y;
      /* held still long enough: the lens has this press */
      if (e.timeStamp - p.t >= HOLD) {
        press.current = null;
        return;
      }
      if (Math.hypot(x, y) < DECIDE) return;
      /* mostly up or down: the page scrolls */
      if (Math.abs(y) > Math.abs(x)) {
        press.current = null;
        return;
      }
      p.swiping = true;
    }
    setDx(x);
  };
  const onUp = () => {
    if (press.current?.swiping && Math.abs(dx) > SWIPE) go(dx < 0 ? 1 : -1);
    press.current = null;
    setDx(0);
  };

  const top = frames(view);

  return (
    <div className={styles.phone}>
      <div
        className={styles.stack}
        data-lb-case-stack=""
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
        onPointerCancel={onUp}
      >
        {leavingFrame && <img key={`leaving-${at}`} src={leavingFrame} className={clsx(styles.stackFile, styles.leaving)} alt="" aria-hidden />}
        {behind.map((v, k) => {
          const src = frames(v);
          return src ? (
            <img key={v.id} src={src} alt="" className={clsx(styles.stackFile, k === 0 ? styles.under2 : styles.under1)} draggable={false} aria-hidden />
          ) : null;
        })}
        <div
          key={view.id}
          className={styles.stackTop}
          style={dx ? { transform: `translateX(${dx}px) rotate(${dx / 30}deg)`, transition: "none" } : undefined}
        >
          {top ? (
            <TouchLoupe src={top} imgClassName={styles.stackFile} />
          ) : (
            <SlideMount view={view} className={styles.phoneMount} />
          )}
        </div>
      </div>
      {cases.length > 1 ? (
        <div className={styles.stackNav}>
          <button type="button" className={styles.stackStep} onClick={() => go(-1)} aria-label="Previous case">
            <ArrowLeft width={18} height={18} aria-hidden />
          </button>
          <p className={styles.stackCount} aria-live="polite">
            {at + 1} of {cases.length}
          </p>
          <button type="button" className={styles.stackStep} onClick={() => go(1)} aria-label="Next case">
            <ArrowRight width={18} height={18} aria-hidden />
          </button>
        </div>
      ) : null}
      <div key={view.id} className={styles.stackCard} data-case-id={view.id}>
        <CaseCard
          view={view}
          mode="frames"
          onPreview={onPreview}
          onHost={onHost}
          canHost={canHost}
          className={styles.card}
          headingId={`lb-case-${view.number}-title`}
        />
      </div>
    </div>
  );
};

const CaseSpread = ({ cases, mode, frames, selectedId, active, onPreview, onHost, canHost }: CaseSpreadProps) => (
  <section
    id="cases"
    className={clsx(styles.spread, selectedId && styles.picking)}
    data-lb-chapter="cases"
    data-lb-active={active ? "" : undefined}
    aria-labelledby="lb-cases-title"
  >
    <div className={styles.pin} data-lb-pin="">
      {mode === "static" ? (
        <div className={styles.table} aria-hidden>
          {cases.map((view) => (
          <SlideMount key={view.id} view={view} className={styles.mount} />
          ))}
        </div>
      ) : null}
      {/* UI-DESIGN: the files and the nav already say what this is; the
          heading is for screen readers and the outline, not another line
          competing with three files under the light */}
      <h2 id="lb-cases-title" className={styles.visuallyHidden}>
        Pick a case: {cases.length} cases on file
      </h2>
      {mode === "frames" ? (
        <PhoneStack cases={cases} frames={frames} onPreview={onPreview} onHost={onHost} canHost={canHost} />
      ) : (
        <ul className={styles.cards}>
          {cases.map((view) => (
            <li key={view.id} className={clsx(styles.slot, view.id === selectedId && styles.selected)} data-case-id={view.id}>
              <CaseCard
                view={view}
                mode={mode}
                selected={view.id === selectedId}
                onPreview={onPreview}
                onHost={onHost}
                canHost={canHost}
                className={styles.card}
                headingId={`lb-case-${view.number}-title`}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  </section>
);

export default CaseSpread;
